import React, { useState, useRef, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MentionOption {
  id: string;
  name: string;
  alias?: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (mentions: string[]) => void;
  placeholder?: string;
  className?: string;
  mentionOptions: MentionOption[];
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  className,
  mentionOptions,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [filteredOptions, setFilteredOptions] = useState<MentionOption[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentMentionStart, setCurrentMentionStart] = useState(-1);
  const [currentQuery, setCurrentQuery] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[2]); // Extract the ID from @[name](id)
    }
    
    return mentions;
  };

  const handleTextChange = (newValue: string) => {
    console.log('MentionTextarea: Text changed to:', newValue);
    onChange(newValue);
    onMentionsChange(extractMentions(newValue));

    if (!textareaRef.current) return;

    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    console.log('MentionTextarea: lastAtIndex:', lastAtIndex, 'cursorPosition:', cursorPosition);

    if (lastAtIndex === -1) {
      console.log('MentionTextarea: No @ found, hiding dropdown');
      setShowDropdown(false);
      return;
    }

    // Check if there's a space or newline between @ and cursor
    const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
    console.log('MentionTextarea: textAfterAt:', textAfterAt);
    if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
      console.log('MentionTextarea: Space/newline found after @, hiding dropdown');
      setShowDropdown(false);
      return;
    }

    // Show dropdown and filter options
    const query = textAfterAt.toLowerCase();
    console.log('MentionTextarea: query:', query, 'mentionOptions:', mentionOptions);
    const filtered = mentionOptions.filter(option =>
      option.name.toLowerCase().includes(query) ||
      (option.alias && option.alias.toLowerCase().includes(query))
    );
    
    console.log('MentionTextarea: filtered options:', filtered);

    setCurrentMentionStart(lastAtIndex);
    setCurrentQuery(query);
    setFilteredOptions(filtered);
    setSelectedIndex(0);
    setShowDropdown(filtered.length > 0);
    
    console.log('MentionTextarea: showDropdown:', filtered.length > 0);

    // Calculate dropdown position
    if (filtered.length > 0) {
      const coords = getCaretCoordinates();
      setDropdownPosition(coords);
    }
  };

  const getCaretCoordinates = () => {
    if (!textareaRef.current) return { top: 0, left: 0 };

    const textarea = textareaRef.current;
    const rect = textarea.getBoundingClientRect();
    
    // Create a mirror div to calculate text position
    const mirror = document.createElement('div');
    const computedStyle = getComputedStyle(textarea);
    
    // Copy styles to mirror
    mirror.style.position = 'absolute';
    mirror.style.visibility = 'hidden';
    mirror.style.whiteSpace = 'pre-wrap';
    mirror.style.wordWrap = 'break-word';
    mirror.style.fontSize = computedStyle.fontSize;
    mirror.style.fontFamily = computedStyle.fontFamily;
    mirror.style.padding = computedStyle.padding;
    mirror.style.border = computedStyle.border;
    mirror.style.width = textarea.offsetWidth + 'px';
    
    const textBeforeCursor = value.substring(0, currentMentionStart);
    mirror.textContent = textBeforeCursor + '@';
    
    document.body.appendChild(mirror);
    
    // Get last character position
    const span = document.createElement('span');
    span.textContent = '@';
    mirror.appendChild(span);
    
    const spanRect = span.getBoundingClientRect();
    const top = spanRect.bottom - rect.top + textarea.scrollTop;
    const left = spanRect.left - rect.left;
    
    document.body.removeChild(mirror);
    
    return { top, left };
  };

  const insertMention = (option: MentionOption) => {
    if (!textareaRef.current) return;

    const beforeMention = value.substring(0, currentMentionStart);
    const afterMention = value.substring(textareaRef.current.selectionStart);
    const mentionText = `@[${option.name}](${option.id})`;
    
    const newValue = beforeMention + mentionText + afterMention;
    const newCursorPosition = beforeMention.length + mentionText.length;
    
    onChange(newValue);
    onMentionsChange(extractMentions(newValue));
    setShowDropdown(false);
    
    // Set cursor position after mention
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % filteredOptions.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (filteredOptions[selectedIndex]) {
          insertMention(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  console.log('MentionTextarea: Rendering with mentionOptions:', mentionOptions.length);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => {
          console.log('MentionTextarea: Native textarea onChange called with:', e.target.value);
          handleTextChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
      />
      
      {showDropdown && filteredOptions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto min-w-48"
          style={{
            top: dropdownPosition.top + 20,
            left: dropdownPosition.left,
          }}
        >
          {filteredOptions.map((option, index) => (
            <Button
              key={option.id}
              variant="ghost"
              className={cn(
                "w-full justify-start text-left px-3 py-2 text-sm rounded-none",
                index === selectedIndex && "bg-muted"
              )}
              onClick={() => insertMention(option)}
            >
              <div className="flex flex-col">
                <span className="font-medium">{option.name}</span>
                {option.alias && option.alias !== option.name && (
                  <span className="text-xs text-muted-foreground">@{option.alias}</span>
                )}
              </div>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};